"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StartExtractComponentsFromDatasheetJobV0Result = exports.StartExtractComponentsFromDatasheetJobV0Failure = exports.StartExtractComponentsFromDatasheetJobV0Success = exports.StartExtractComponentsFromDatasheetJobV0Request = exports.StartExtractComponentsFromDatasheetJobResult = exports.StartExtractComponentsFromDatasheetJobSuccess = exports.StartExtractComponentsFromDatasheetJobFailure = exports.StartExtractComponentsFromDatasheetJobRequest = exports.extractComponentsFromDatasheetJobSchema = exports.extractComponentsFromDatasheetCompleteSchema = exports.extractComponentsFromDatasheetJobUidSchema = void 0;
exports.extractComponentsFromDatasheetJobUid = extractComponentsFromDatasheetJobUid;
exports.createExtractComponentsFromDatasheetJobWithWaitingForUploadStatus = createExtractComponentsFromDatasheetJobWithWaitingForUploadStatus;
exports.createExtractComponentsFromDatasheetJobWithPendingStatus = createExtractComponentsFromDatasheetJobWithPendingStatus;
const tslib_1 = require("tslib");
const zod_1 = tslib_1.__importDefault(require("zod"));
const identifier_1 = require("../identifier");
const shared_1 = require("./shared");
const status_1 = require("./status");
function extractComponentsFromDatasheetJobUid(id) {
    return `get-ds-comp-${id}`;
}
exports.extractComponentsFromDatasheetJobUidSchema = zod_1.default.custom((s) => typeof s === "string" && s.startsWith("get-ds-comp"));
/**
 * Schema for status of this particular job
 */
exports.extractComponentsFromDatasheetCompleteSchema = (0, status_1.createCompleteStatusSchema)(zod_1.default.array(shared_1.importableComponentInfoSchema));
const ExtractComponentsFromDatasheetJobStatus = zod_1.default.discriminatedUnion("type", [
    status_1.failedStatusSchema,
    status_1.runningStatusSchema,
    status_1.pendingStatusSchema,
    status_1.waitingForUploadStatusSchema,
    status_1.verifiedStatusSchema,
    exports.extractComponentsFromDatasheetCompleteSchema,
]);
/**
 * Schema of the job
 */
exports.extractComponentsFromDatasheetJobSchema = zod_1.default.object({
    // NOTE: We polished the type and schema name for this job to make
    // them more consistent, but decided to keep the type literal unchanged
    // because changing it requires backward compatibility across cloud
    // functions, storage triggers and Temporal, its a bit risky.
    // As long as the type/schema names are consistent, we are good
    type: zod_1.default.literal("getDatasheetComponent"),
    submittedByUserUid: zod_1.default.string(),
    // TODO: Rename this to `importArtifactUid`
    pdfUid: shared_1.importArtifactUidSchema,
    contentMimeType: zod_1.default
        .enum([
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ])
        .default("application/pdf"),
    jobUid: exports.extractComponentsFromDatasheetJobUidSchema,
    userOrganizationUid: identifier_1.optionalOrganizationUid,
    workflowId: zod_1.default.string(),
    jobStatus: ExtractComponentsFromDatasheetJobStatus,
});
/**
 * Schema of request for the `startExtractDatasheetComponentsJob` cloud function
 */
exports.StartExtractComponentsFromDatasheetJobRequest = zod_1.default.object({
    organizationUid: identifier_1.optionalOrganizationUid,
    contentMimeType: zod_1.default
        .enum([
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ])
        .default("application/pdf"),
});
/**
 * Schema for the result returned by the `startExtractDatasheetComponentsJob` cloud function
 */
exports.StartExtractComponentsFromDatasheetJobFailure = zod_1.default.object({
    type: zod_1.default.literal("failure"),
    failureReason: zod_1.default.string(),
});
exports.StartExtractComponentsFromDatasheetJobSuccess = zod_1.default.object({
    type: zod_1.default.literal("success"),
    uploadUrl: zod_1.default.string(),
    jobUid: exports.extractComponentsFromDatasheetJobUidSchema,
});
exports.StartExtractComponentsFromDatasheetJobResult = zod_1.default.discriminatedUnion("type", [
    exports.StartExtractComponentsFromDatasheetJobFailure,
    exports.StartExtractComponentsFromDatasheetJobSuccess,
]);
/**
 * Schema of request for the `startExtractOrderingInformationJob` cloud function
 *
 * This is the V0 version of StartExtractDatasheetComponentsJob endpoint, where the cloud function
 * does the upload.
 *
 * As of 2024/06/26, the corresponding function is only called from UI, and we plan to migrate that
 * to call the `startExtractDatasheetComponentsJob` in PR:
 * https://github.com/buildwithflux/flux-app/pull/11792
 */
exports.StartExtractComponentsFromDatasheetJobV0Request = zod_1.default
    .object({
    fileContents: zod_1.default.string().optional(),
    pdfFileContents: zod_1.default.string().optional(),
    organizationUid: identifier_1.optionalOrganizationUid,
    contentMimeType: zod_1.default
        .enum([
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ])
        .default("application/pdf"),
})
    // TODO: remove this once `pdfFileContents` can be removed from the request
    .transform((request) => ({
    fileContents: request.fileContents ?? request.pdfFileContents,
    organizationUid: request.organizationUid,
    contentMimeType: request.contentMimeType,
}));
exports.StartExtractComponentsFromDatasheetJobV0Success = zod_1.default.object({
    jobUid: exports.extractComponentsFromDatasheetJobUidSchema,
    type: zod_1.default.literal("success"),
});
exports.StartExtractComponentsFromDatasheetJobV0Failure = zod_1.default.object({
    failureReason: zod_1.default.string(),
    type: zod_1.default.literal("failure"),
});
exports.StartExtractComponentsFromDatasheetJobV0Result = zod_1.default.discriminatedUnion("type", [
    exports.StartExtractComponentsFromDatasheetJobV0Success,
    exports.StartExtractComponentsFromDatasheetJobV0Failure,
]);
/**
 * Creates a job for ExtractComponentsFromDatasheet with `WaitingForUpload`, this is the
 * very first state of the job in the new flow we implemented starting from the
 * `startExtractComponentsFromDatasheet` endpoint.
 *
 * Difference is we dont need `pdfUid` nor `workflowUid` at this point because we
 * are still waiting for user to upload the pdf, and only after that we will start
 * the workflow
 */
function createExtractComponentsFromDatasheetJobWithWaitingForUploadStatus(submittedByUserUid, organizationUid, jobUid, uploadLocation, contentMimeType) {
    return {
        type: "getDatasheetComponent",
        submittedByUserUid,
        userOrganizationUid: organizationUid,
        jobUid,
        jobStatus: (0, status_1.jobWaitingForUploadStatus)(uploadLocation),
        contentMimeType,
        // Setting these to empty, to keep using the existing schema instead of introducing
        // a new one... we can clean this up when the new flow is deployed and stable
        pdfUid: "pdf-upload-",
        workflowId: "",
    };
}
/**
 * Creates a job for GetDatasheetComponents with `PendingStatus`, this is the
 * very first state of the job in the old flow we implemented in `doStartGetImportableComponentsJob`
 */
function createExtractComponentsFromDatasheetJobWithPendingStatus(submittedByUserUid, pdfUid, jobUid, workflowId, contentMimeType) {
    return {
        type: "getDatasheetComponent",
        submittedByUserUid,
        pdfUid,
        jobUid,
        workflowId,
        contentMimeType,
        jobStatus: (0, status_1.jobPendingStatus)(),
    };
}
//# sourceMappingURL=extractComponentsFromDatasheet.js.map